<script>
import { InfoOutlined } from '@ant-design/icons-vue';

export default {
  name: 'SubscriptionDetailsCard',
  components: {
    InfoOutlined,
  },
  emits: ['order'],
  props: {
    subscription: {
      type: Object,
      required: true,
    },
  },
  setup(props, context) {
    const order = () => {
      context.emit('order', props.subscription);
    };

    return {
      order,
    };
  },
};
</script>

<template>
  <a-card
      class="subscription-details-card"
      :bordered="true"
      :style="{
        '--subscription-color': subscription.color,
        '--subscription-tag-background': subscription.tagBackground,
        '--subscription-tag-border': subscription.tagBorder,
      }"
  >
    <div class="subscription-details-card__body">
      <div>
        <h3>{{ subscription.title }}</h3>
        <p class="subscription-details-card__description">
          {{ subscription.description }}
        </p>

        <div class="subscription-details-card__tags">
          <a-tag
              v-for="tag in subscription.tags"
              :key="tag"
              class="subscription-details-card__tag"
          >
            {{ tag }}
          </a-tag>
        </div>

        <a-divider />

        <ul class="subscription-details-card__features">
          <li
              v-for="feature in subscription.features"
              :key="feature"
          >
            <span class="subscription-details-card__feature-mark">✓</span>
            <span>{{ feature }}</span>
          </li>
        </ul>
      </div>

      <a-divider class="subscription-details-card__footer-divider" />

      <div class="subscription-details-card__footer">
        <div>
          <div class="subscription-details-card__price">
            {{ subscription.price }}
          </div>
          <div class="subscription-details-card__period">
            {{ subscription.period }}
          </div>
        </div>

        <div class="subscription-details-card__actions">
          <a-button class="secondary-button subscription-details-card__tariff-button">
            <span class="subscription-details-card__tariff-text">
              О тарифе
            </span>

            <InfoOutlined class="subscription-details-card__tariff-icon" />
          </a-button>
          <a-button
              class="subscription-details-card__order"
              @click="order"
          >
            Оформить
          </a-button>
        </div>
      </div>
    </div>
  </a-card>
</template>

<style scoped lang="scss">
.subscription-details-card {
  height: 100%;
  overflow: hidden;
  border-color: var(--subscription-tag-background);
  background: #ffffff;
}

.subscription-details-card::before {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 4px;
  background: var(--subscription-tag-background);
}

.subscription-details-card :deep(.ant-card-body) {
  height: 100%;
}

.subscription-details-card__body {
  display: flex;
  flex-direction: column;
  min-height: 312px;
  height: 100%;
}

h3 {
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.subscription-details-card__description {
  min-height: 58px;
  margin-bottom: 16px;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.subscription-details-card__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.subscription-details-card__tag {
  margin-right: 0;
  color: var(--subscription-color);
  border-color: var(--subscription-tag-border);
  background: var(--subscription-tag-background);
}

.subscription-details-card :deep(.ant-tag) {
  font-family: 'Open Sans' !important;
}

.subscription-details-card :deep(.ant-divider-horizontal) {
  margin: 16px 0;
}

.subscription-details-card :deep(.subscription-details-card__footer-divider) {
  width: calc(100% + 40px);
  min-width: calc(100% + 40px);
  margin: 16px -20px;
}

.subscription-details-card__features {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 145px;
  margin: 0;
  padding: 0;
  list-style: none;
  color: var(--subscription-color);
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
}

.subscription-details-card__features li {
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr);
  column-gap: 8px;
}

.subscription-details-card__feature-mark {
  line-height: 120%;
}

.subscription-details-card__footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-top: auto;
}

.subscription-details-card__price {
  color: var(--subscription-color);
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
}

.subscription-details-card__period {
  color: rgba(0, 0, 0, 0.45);
  font-weight: 400;
  font-size: 12px;
  line-height: 110%;
}

.subscription-details-card__actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.subscription-details-card__tariff-icon {
  display: none;
}

.subscription-details-card__order {
  color: #ffffff !important;
  border-color: var(--subscription-color) !important;
  background: var(--subscription-color) !important;
}

.subscription-details-card__order:hover,
.subscription-details-card__order:active,
.subscription-details-card__order:focus {
  color: #ffffff !important;
  border-color: var(--subscription-color) !important;
  background: var(--subscription-color) !important;
}

@media (max-width: $mobile-breakpoint) {
  .subscription-details-card__body {
    min-height: auto;
  }

  .subscription-details-card__description {
    min-height: auto;
  }

  .subscription-details-card__features {
    min-height: auto;
  }

  .subscription-details-card__footer {
    align-items: flex-end;
  }

  .subscription-details-card__tariff-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    min-width: 32px;
    height: 32px;
    padding: 0;
    line-height: 1;
  }

  .subscription-details-card__tariff-text {
    display: none;
  }

  .subscription-details-card__tariff-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: rgba(0, 0, 0, 0.45);
    font-size: 18px;
    line-height: 1;
    vertical-align: 0;
  }

  .subscription-details-card__tariff-button :deep(.anticon) {
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    margin-inline-start: 0;
  }

  .subscription-details-card__tariff-button :deep(svg) {
    display: block;
  }

  .subscription-details-card__order {
    height: 32px;
  }
}
</style>
